import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonAlert,
  IonButtons,
  IonBackButton,
  IonToast,
  IonAccordion,
  IonAccordionGroup,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonThumbnail,
} from '@ionic/react';
import {
  barbellOutline,
  bookmarkOutline,
  bodyOutline,
  timeOutline,
  trashOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { firestore } from '../../../firebase';
import { doc, getDoc, DocumentReference } from 'firebase/firestore';
import { machineSessionRepository, workoutRepository } from '../../../repositories/LocalRepositories';
import {
  convertFirestoreTimestampToDate,
  calculateDuration,
  deleteTrainingSession,
} from '../../../utils/TrainingSessionUtils';
import { getExerciseTrackingMode, trackingLabel } from '../../../utils/ExerciseTracking';
import { useAuth } from '../../../auth';
import { useParams, useHistory } from 'react-router-dom';
import {
  Machines,
  TrainingSessions,
  MachineSession,
} from '../../../datamodels';
import { saveRoutine } from '../../../services/RoutineService';
import './TrainingSessionDetails.css';
import { getMachineImageUrl } from '../../../utils/MachineImageUtils';

interface MachineDetails {
  machine: Machines;
  exerciseName: string | null;
  sets: MachineSession['sets'];
  musclesTrained: string[]; // <-- added
}

const TrainingSessionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [trainingSession, setTrainingSession] =
    useState<TrainingSessions | null>(null);
  const [machineDetails, setMachineDetails] = useState<MachineDetails[]>([]);

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [routineName, setRoutineName] = useState('');
  const [showToast, setShowToast] = useState<{
    isOpen: boolean;
    message: string;
  }>({ isOpen: false, message: '' });

  const history = useHistory();
  const { userId } = useAuth();

  useEffect(() => {
    console.debug('[TrainingSessionDetails] Session ID:', id);
  }, [id]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!userId) {
        console.error('User is not authenticated');
        return;
      }

      const sessionData = await workoutRepository.get(id);
      if (!sessionData || sessionData.user_id !== userId) return;
      setTrainingSession(sessionData);

      const promises = sessionData.machine_sessions.map(async msId => {
        const msData = await machineSessionRepository.get(msId);
        if (!msData) return null;
        const machineRef = msData.machine_ref as DocumentReference;

        const machineDoc = await getDoc(machineRef);
        if (!machineDoc.exists()) return null;

        const machine = { id: machineDoc.id, ...machineDoc.data() } as Machines;

        const exerciseName = msData.exercise_name || null;

        // ---- derive muscles for this machine session ----
        const exercises = Array.isArray(machine.exercises)
          ? machine.exercises
          : [];

        let muscles: string[] = [];

        if (exerciseName) {
          const normalizedExerciseName = exerciseName.trim().toLocaleLowerCase();
          const ex = exercises.find(e => e?.name?.trim().toLocaleLowerCase() === normalizedExerciseName);
          muscles = Array.isArray(ex?.muscles) ? ex!.muscles : [];
        } else {
          // fallback: aggregate all muscles for this machine
          const set = new Set<string>();
          exercises.forEach(e => {
            const m = Array.isArray(e?.muscles) ? e.muscles : [];
            m.forEach(x => {
              const t = typeof x === 'string' ? x.trim() : '';
              if (t) set.add(t);
            });
          });
          muscles = Array.from(set);
        }

        // normalize + dedupe
        const musclesTrained = Array.from(
          new Set(
            muscles
              .map(m => (typeof m === 'string' ? m.trim() : ''))
              .filter(Boolean)
          )
        );

        return {
          machine,
          exerciseName,
          sets: msData.sets,
          musclesTrained,
        } as MachineDetails;
      });

      const detailed = (await Promise.all(promises)).filter(
        (md): md is MachineDetails => md !== null
      );
      setMachineDetails(detailed);
    };

    fetchSession();
  }, [id, userId]);

  const handleDelete = async () => {
    if (!userId) {
      console.error('User is not authenticated.');
      return;
    }
    try {
      await deleteTrainingSession(id, userId);
      history.push('/my/sessions');
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleSaveRoutine = async (name: string) => {
    if (!userId) {
      console.error('User is not authenticated');
      return;
    }
    if (!name.trim()) return;

    try {
      const msForRoutine: MachineSession[] = machineDetails.map(
        md =>
          ({
            machine_ref: doc(
              firestore,
              'machines',
              md.machine.id
            ) as DocumentReference,
            exercise_name: md.exerciseName ?? md.machine.title,
            sets: md.sets,
          }) as unknown as MachineSession
      );

      await saveRoutine(userId, name, msForRoutine);
      setShowToast({ isOpen: true, message: `Routine "${name}" saved!` });
    } catch (err) {
      console.error('Error saving routine:', err);
      setShowToast({ isOpen: true, message: 'Failed to save routine.' });
    } finally {
      setShowSaveAlert(false);
      setRoutineName('');
    }
  };

  const startDate = trainingSession
    ? convertFirestoreTimestampToDate(trainingSession.start_date)
    : null;
  const endDate = trainingSession
    ? convertFirestoreTimestampToDate(trainingSession.end_date)
    : null;

  const formattedStartDate = startDate
    ? startDate.toLocaleString()
    : 'No Start Date';

  const duration =
    startDate && endDate
      ? calculateDuration(startDate, endDate)
      : 'Duration not available';

  // Session-level muscles (union across all machine sessions)
  const sessionMusclesTrained = useMemo(() => {
    const set = new Set<string>();
    machineDetails.forEach(md => {
      (md.musclesTrained || []).forEach(m => set.add(m));
    });
    return Array.from(set);
  }, [machineDetails]);

  const sessionMusclesText =
    sessionMusclesTrained.length > 0 ? sessionMusclesTrained.join(', ') : '-';

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my/statistics" text="" />
          </IonButtons>
          <IonTitle>Workout Details</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="session-details-content">
        <section className="session-summary" aria-label="Workout summary">
          <span className="session-summary__eyebrow">Completed workout</span>
          <h1>{formattedStartDate}</h1>
          <div className="session-summary__stats">
            <div>
              <IonIcon icon={timeOutline} aria-hidden="true" />
              <span>Duration</span>
              <strong>{duration}</strong>
            </div>
            <div>
              <IonIcon icon={barbellOutline} aria-hidden="true" />
              <span>Exercises</span>
              <strong>{machineDetails.length}</strong>
            </div>
          </div>
          <div className="session-summary__muscles">
            <IonIcon icon={bodyOutline} aria-hidden="true" />
            <div>
              <span>Muscles trained</span>
              <strong>{sessionMusclesText}</strong>
            </div>
          </div>
        </section>

        <div className="session-section-heading">
          <span>Workout log</span>
          <h2>Machines &amp; sets</h2>
        </div>

        {machineDetails.length > 0 ? (
          <div className="session-accordion-card">
            <IonAccordionGroup>
            {machineDetails.map(
              ({ machine, sets, exerciseName }, machineIndex) => {
                const subtitle =
                  exerciseName && exerciseName !== machine.title
                    ? exerciseName
                    : `${sets.length} ${sets.length === 1 ? 'set' : 'sets'}`;

                const imageSrc = getMachineImageUrl(machine);
                const exercise = machine.exercises?.find(item => item.name.trim().toLocaleLowerCase() === exerciseName?.trim().toLocaleLowerCase());
                const trackingMode = getExerciseTrackingMode(machine.title, exercise || (exerciseName ? { name: exerciseName } : undefined));

                return (
                  <IonAccordion
                    key={`${machine.id}-${machineIndex}`}
                    value={`machine-${machineIndex}`}
                  >
                    <IonItem slot="header" color="light" className="session-machine-header">
                      <IonThumbnail slot="start" className="session-machine-thumb">
                        <IonImg
                          src={imageSrc}
                          alt={machine.title || 'Workout machine'}
                        />
                      </IonThumbnail>
                      <IonLabel className="session-machine-label">
                        <div className="session-machine-title">
                          {machine.title || 'Unnamed Machine'}
                        </div>
                        <div className="session-machine-subtitle">{subtitle}</div>
                      </IonLabel>
                    </IonItem>

                    <div className="session-sets" slot="content">
                      <div className="session-set-row session-set-row--header">
                        <span>Set</span>
                        <span>Reps</span>
                        <span>{trackingLabel(trackingMode)}</span>
                      </div>
                      {sets.length > 0 ? (
                        sets.map((set, setIndex) => (
                          <div className="session-set-row" key={`${set.set_number}-${setIndex}`}>
                            <span>{set.set_number || setIndex + 1}</span>
                            <strong>{set.reps}</strong>
                            <strong>{trackingMode === 'bodyweight' && !set.weight ? 'Bodyweight' : `${set.weight} kg`}</strong>
                          </div>
                        ))
                      ) : (
                        <p className="session-sets__empty">No sets recorded.</p>
                      )}
                    </div>
                  </IonAccordion>
                );
              }
            )}
            </IonAccordionGroup>
          </div>
        ) : (
          <div className="session-empty">No machines were recorded for this workout.</div>
        )}

        <div className="session-actions">
          <IonButton
            expand="block"
            onClick={() => setShowSaveAlert(true)}
            disabled={machineDetails.length === 0}
          >
            <IonIcon icon={bookmarkOutline} slot="start" />
            Save as routine
          </IonButton>

          <IonButton
            expand="block"
            fill="outline"
            color="danger"
            onClick={() => setShowDeleteAlert(true)}
          >
            <IonIcon icon={trashOutline} slot="start" />
            Delete workout
          </IonButton>
        </div>

        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Delete Session"
          message="Are you sure you want to delete this session?"
          buttons={[
            {
              text: 'No',
              role: 'cancel',
              handler: () => setShowDeleteAlert(false),
            },
            { text: 'Yes', handler: handleDelete },
          ]}
        />

        <IonAlert
          isOpen={showSaveAlert}
          onDidDismiss={() => setShowSaveAlert(false)}
          header="Name your routine"
          inputs={[
            {
              name: 'routineName',
              type: 'text',
              placeholder: 'e.g. My Leg Day',
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => setShowSaveAlert(false),
            },
            {
              text: 'Save',
              handler: data => handleSaveRoutine(data.routineName || ''),
            },
          ]}
        />

        <IonToast
          isOpen={showToast.isOpen}
          message={showToast.message}
          duration={2000}
          position="top"
          onDidDismiss={() => setShowToast({ ...showToast, isOpen: false })}
        />
      </IonContent>
    </IonPage>
  );
};

export default TrainingSessionDetails;
